// Checkout1.js - Contains functions for creating a Checkout section
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates a complete Checkout section with nested elements
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the checkout is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The checkout elements
 */
export const createCheckoutElements = (
  getHierarchyName,
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const checkoutSectionClass = 'checkout1-section';
  const checkoutContainerClass = 'checkout1-container';
  const checkoutFormClass = 'checkout1-form';
  const checkoutSummaryClass = 'checkout1-summary';
  const formGroupClass = 'checkout1-form-group';
  const inputClass = 'checkout1-input';
  const labelClass = 'checkout1-label';
  const submitBtnClass = 'checkout1-submit-btn';
  const summaryItemClass = 'checkout1-summary-item';
  const totalClass = 'checkout1-total';

  // Desktop Styles (default)
  const checkoutSectionStyles = {
    position: 'relative',
    padding: '60px 20px',
    backgroundColor: '#ffffff',
    color: '#333333',
    width: '100%',
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start'
  };

  const checkoutContainerStyles = {
    display: 'grid',
    gridTemplateColumns: '1fr 400px',
    gap: '40px',
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto'
  };

  const checkoutFormStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    padding: '30px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
  };

  const checkoutSummaryStyles = {
    padding: '30px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    position: 'sticky',
    top: '20px'
  };

  const formGroupStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  };

  const inputStyles = {
    padding: '12px 16px',
    border: '1px solid #dee2e6',
    borderRadius: '4px',
    fontSize: '16px',
    transition: 'border-color 0.2s ease',
    width: '100%'
  };

  const labelStyles = {
    fontSize: '14px',
    fontWeight: '500',
    color: '#495057'
  };

  const submitBtnStyles = {
    padding: '16px 24px',
    backgroundColor: '#2563eb',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    marginTop: '24px',
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    ':hover': {
      backgroundColor: '#1d4ed8'
    }
  };

  const summaryItemStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px 0',
    borderBottom: '1px solid #dee2e6'
  };

  const totalStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '20px 0',
    marginTop: '20px',
    borderTop: '2px solid #212529',
    fontWeight: '700',
    fontSize: '18px'
  };

  // Tablet Styles
  const tabletCheckoutContainerStyles = {
    ...checkoutContainerStyles,
    gridTemplateColumns: '1fr',
    gap: '30px'
  };

  // Mobile Styles
  const mobileCheckoutContainerStyles = {
    ...checkoutContainerStyles,
    padding: '20px',
    gap: '24px'
  };

  const mobileCheckoutFormStyles = {
    ...checkoutFormStyles,
    padding: '20px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(checkoutSectionClass, checkoutSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(checkoutContainerClass, checkoutContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(checkoutFormClass, checkoutFormStyles);
    newRegistry[DEVICES.DESKTOP].set(checkoutSummaryClass, checkoutSummaryStyles);
    newRegistry[DEVICES.DESKTOP].set(formGroupClass, formGroupStyles);
    newRegistry[DEVICES.DESKTOP].set(inputClass, inputStyles);
    newRegistry[DEVICES.DESKTOP].set(labelClass, labelStyles);
    newRegistry[DEVICES.DESKTOP].set(submitBtnClass, submitBtnStyles);
    newRegistry[DEVICES.DESKTOP].set(summaryItemClass, summaryItemStyles);
    newRegistry[DEVICES.DESKTOP].set(totalClass, totalStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(checkoutContainerClass, tabletCheckoutContainerStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(checkoutContainerClass, mobileCheckoutContainerStyles);
    newRegistry[DEVICES.MOBILE].set(checkoutFormClass, mobileCheckoutFormStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    checkoutSectionClass,
    checkoutContainerClass,
    checkoutFormClass,
    checkoutSummaryClass,
    formGroupClass,
    inputClass,
    labelClass,
    submitBtnClass,
    summaryItemClass,
    totalClass
  ]));

  // Generate IDs for all elements
  const checkoutSectionId = Date.now() + 2355436567;
  const containerID = Date.now() + 1324324;
  const formID = Date.now() + 2123432134;
  const summaryID = Date.now() + 31233544325;
  
  // Generate label and input IDs
  const fullNameLabelId = Date.now() + 9234234234;
  const fullNameInputId = Date.now() + 1432523452340;
  const emailLabelId = Date.now() + 12345234566571;
  const emailInputId = Date.now() + 1245654767;
  const addressLabelId = Date.now() + 13567567564;
  const addressInputId = Date.now() + 14345345234;
  const cardLabelId = Date.now() + 1534556757;
  const cardInputId = Date.now() + 1654643534;
  const submitBtnId = Date.now() + 88768768;

  const newElements = [
    // Main Checkout Section Container
    {
      id: checkoutSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Checkout Section',
      parentId: null,
      className: checkoutSectionClass,
      styles: {},
      children: [containerID],
      text: '',
      attributes: {
        'data-element': 'checkout-section',
        'data-component': 'section',
        'data-section-type': 'checkout',
        'data-page-id': currentPageId
      }
    },
    
    // Container for form and summary
    {
      id: containerID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Checkout Container',
      parentId: checkoutSectionId,
      hierarchyName: "Checkout Container",
      className: checkoutContainerClass,
      styles: {},
      children: [formID, summaryID],
      text: '',
      attributes: {
        'data-element': 'checkout-container'
      }
    },
    
    // Checkout Form
    {
      id: formID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'form',
      name: 'checkout-form',
      parentId: containerID,
      hierarchyName: "Checkout Form",
      className: checkoutFormClass,
      styles: {},
      children: [
        fullNameLabelId,
        fullNameInputId,
        emailLabelId,
        emailInputId,
        addressLabelId,
        addressInputId,
        cardLabelId,
        cardInputId,
        submitBtnId
      ],
      text: '',
      attributes: {
        'data-element': 'checkout-form',
        'name': 'checkout-form',
      }
    },

    // Full Name Label
    {
      id: fullNameLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Full Name Label',
      parentId: formID,
      hierarchyName: "Full Name Label",
      className: labelClass,
      styles: {
        display: 'block',
        marginBottom: '8px'
      },
      children: [],
      text: 'Full Name',
      attributes: {
        'for': 'name'
      }
    },
    // Full Name Input
    {
      id: fullNameInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Full Name Input',
      parentId: formID,
      hierarchyName: "Full Name Input",
      className: inputClass,
      styles: {
        marginBottom: '16px'
      },
      children: [],
      text: '',
      attributes: {
        'type': 'text',
        'id': 'name',
        'name': 'fullName',
        'required': 'true',
        'data-field': 'checkout-fullname',
      }
    },

    // Email Label
    {
      id: emailLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Email Label',
      parentId: formID,
      hierarchyName: "Email Label",
      className: labelClass,
      styles: {
        display: 'block',
        marginBottom: '8px'
      },
      children: [],
      text: 'Email Address',
      attributes: {
        'for': 'email'
      }
    },
    // Email Input
    {
      id: emailInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Email Input',
      parentId: formID,
      hierarchyName: "Email Input",
      className: inputClass,
      styles: {
        marginBottom: '16px'
      },
      children: [],
      text: '',
      attributes: {
        'type': 'email',
        'id': 'email',
        'name': 'email',
        'required': 'true',
        'data-field': 'checkout-email',
      }
    },

    // Address Label
    {
      id: addressLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Address Label',
      parentId: formID,
      hierarchyName: "Address Label",
      className: labelClass,
      styles: {
        display: 'block',
        marginBottom: '8px'
      },
      children: [],
      text: 'Shipping Address',
      attributes: {
        'for': 'address'
      }
    },
    // Address Input
    {
      id: addressInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Address Input',
      parentId: formID,
      hierarchyName: "Address Input",
      className: inputClass,
      styles: {
        marginBottom: '16px'
      },
      children: [],
      text: '',
      attributes: {
        'type': 'text',
        'id': 'address',
        'name': 'address',
        'required': 'true',
        'data-field': 'checkout-address',
      }
    },

    // Card Label
    {
      id: cardLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Card Label',
      parentId: formID,
      hierarchyName: "Card Label",
      className: labelClass,
      styles: {
        display: 'block',
        marginBottom: '8px'
      },
      children: [],
      text: 'Card Number',
      attributes: {
        'for': 'card'
      }
    },
    // Card Input
    {
      id: cardInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Card Input',
      parentId: formID,
      hierarchyName: "Card Input",
      className: inputClass,
      styles: {
        marginBottom: '16px'
      },
      children: [],
      text: '',
      attributes: {
        'type': 'text',
        'id': 'card',
        'name': 'card',
        'required': 'true',
        'data-field': 'checkout-card',
      }
    },

    // Submit Button
    {
      id: submitBtnId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'submit',
      name: 'Submit Button',
      parentId: formID,
      hierarchyName: "Submit Button",
      className: submitBtnClass,
      styles: {},
      children: [],
      text: 'Complete Purchase',
      attributes: {
        'type': 'submit',
        'data-element': 'checkout-submit'
      }
    },
    
    // Order Summary
    {
      id: summaryID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Order Summary',
      parentId: containerID,
      hierarchyName: "Order Summary",
      className: checkoutSummaryClass,
      styles: {},
      children: [
        summaryID + 1,  // Title
        summaryID + 8,  // Order Items Container
        summaryID + 9   // Summary Totals Container
      ],
      text: '',
      attributes: {
        'data-element': 'checkout-summary'
      }
    },

    // Summary Title
    {
      id: summaryID + 1,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Summary Title',
      parentId: summaryID,
      hierarchyName: "Summary Title",
      className: '',
      styles: {
        marginBottom: '24px',
        fontSize: '24px',
        fontWeight: '600'
      },
      children: [],
      text: 'Order Summary',
      attributes: {}
    },

    // Order Items Container
    {
      id: summaryID + 8,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Order Items Container',
      parentId: summaryID,
      hierarchyName: "Order Items Container",
      className: 'checkout1-order-items',
      styles: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        marginBottom: '24px'
      },
      children: [
        summaryID + 2,  // Product 1
        summaryID + 6,  // Product 2
        summaryID + 7   // Product 3
      ],
      text: '',
      attributes: {
        'data-element': 'checkout-order-items'
      }
    },

    // Summary Totals Container
    {
      id: summaryID + 9,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Summary Totals Container',
      parentId: summaryID,
      hierarchyName: "Summary Totals Container",
      className: 'checkout1-summary-totals',
      styles: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        borderTop: '1px solid #dee2e6',
        paddingTop: '20px'
      },
      children: [
        summaryID + 3,  // Shipping
        summaryID + 4,  // Tax
        summaryID + 5   // Total
      ],
      text: '',
      attributes: {
        'data-element': 'checkout-summary-totals'
      }
    },

    // Product Item 1
    {
      id: summaryID + 2,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'checkout-order-item',
      name: 'Product Item 1',
      parentId: summaryID,
      hierarchyName: "Product Item 1",
      className: summaryItemClass,
      styles: {},
      children: [summaryID + 21, summaryID + 22, summaryID + 23],
      text: '',
      attributes: {
        'data-element': 'checkout-order-item',
        'data-template': 'true'
      }
    },
    {
      id: summaryID + 21,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Product Name 1',
      parentId: summaryID + 2,
      hierarchyName: "Product Name 1",
      className: '',
      styles: {
        flex: '1',
        fontSize: '15px'
      },
      children: [],
      text: 'Ergonomic Chair',
      attributes: {
        'data-field': 'checkout-product-name'
      }
    },
    {
      id: summaryID + 22,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Product Quantity 1',
      parentId: summaryID + 2,
      hierarchyName: "Product Quantity 1",
      className: '',
      styles: {
        marginRight: '16px',
        color: '#666',
        minWidth: '32px',
        textAlign: 'center'
      },
      children: [],
      text: 'x2',
      attributes: {
        'data-field': 'checkout-product-quantity'
      }
    },
    {
      id: summaryID + 23,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Product Price 1',
      parentId: summaryID + 2,
      hierarchyName: "Product Price 1",
      className: '',
      styles: {
        fontWeight: '500',
        minWidth: '80px',
        textAlign: 'right'
      },
      children: [],
      text: '$0.00',
      attributes: {
        'data-field': 'checkout-product-price'
      }
    },

    // Product Item 2
    {
      id: summaryID + 6,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'checkout-order-item',
      name: 'Product Item 2',
      parentId: summaryID,
      hierarchyName: "Product Item 2",
      className: summaryItemClass,
      styles: {},
      children: [summaryID + 61, summaryID + 62, summaryID + 63],
      text: '',
      attributes: {
        'data-element': 'checkout-order-item',
        'data-template': 'true'
      }
    },
    {
      id: summaryID + 61,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Product Name 2',
      parentId: summaryID + 6,
      hierarchyName: "Product Name 2",
      className: '',
      styles: {
        flex: '1',
        fontSize: '15px'
      },
      children: [],
      text: 'Desk Lamp',
      attributes: {
        'data-field': 'checkout-product-name'
      }
    },
    {
      id: summaryID + 62,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Product Quantity 2',
      parentId: summaryID + 6,
      hierarchyName: "Product Quantity 2",
      className: '',
      styles: {
        marginRight: '16px',
        color: '#666',
        minWidth: '32px',
        textAlign: 'center'
      },
      children: [],
      text: 'x1',
      attributes: {
        'data-field': 'checkout-product-quantity'
      }
    },
    {
      id: summaryID + 63,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Product Price 2',
      parentId: summaryID + 6,
      hierarchyName: "Product Price 2",
      className: '',
      styles: {
        fontWeight: '500',
        minWidth: '80px',
        textAlign: 'right'
      },
      children: [],
      text: '$0.00',
      attributes: {
        'data-field': 'checkout-product-price'
      }
    },

    // Product Item 3
    {
      id: summaryID + 7,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'checkout-order-item',
      name: 'Product Item 3',
      parentId: summaryID,
      hierarchyName: "Product Item 3",
      className: summaryItemClass,
      styles: {},
      children: [summaryID + 71, summaryID + 72, summaryID + 73],
      text: '',
      attributes: {
        'data-element': 'checkout-order-item',
        'data-template': 'true'
      }
    },
    {
      id: summaryID + 71,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Product Name 3',
      parentId: summaryID + 7,
      hierarchyName: "Product Name 3",
      className: '',
      styles: {
        flex: '1',
        fontSize: '15px'
      },
      children: [],
      text: 'Wireless Mouse',
      attributes: {
        'data-field': 'checkout-product-name'
      }
    },
    {
      id: summaryID + 72,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Product Quantity 3',
      parentId: summaryID + 7,
      hierarchyName: "Product Quantity 3",
      className: '',
      styles: {
        marginRight: '16px',
        color: '#666',
        minWidth: '32px',
        textAlign: 'center'
      },
      children: [],
      text: 'x2',
      attributes: {
        'data-field': 'checkout-product-quantity'
      }
    },
    {
      id: summaryID + 73,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Product Price 3',
      parentId: summaryID + 7,
      hierarchyName: "Product Price 3",
      className: '',
      styles: {
        fontWeight: '500',
        minWidth: '80px',
        textAlign: 'right'
      },
      children: [],
      text: '$0.00',
      attributes: {
        'data-field': 'checkout-product-price'
      }
    },

    // Shipping Item
    {
      id: summaryID + 3,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Shipping Item',
      parentId: summaryID + 9,
      hierarchyName: "Shipping Item",
      className: summaryItemClass,
      styles: {},
      children: [summaryID + 31, summaryID + 32],
      text: '',
      attributes: {
        'data-element': 'shipping-row'
      }
    },
    {
      id: summaryID + 31,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Shipping Label',
      parentId: summaryID + 3,
      hierarchyName: "Shipping Label",
      className: '',
      styles: {
        flex: '1'
      },
      children: [],
      text: 'Shipping',
      attributes: {
        'data-field': 'shipping-label'
      }
    },
    {
      id: summaryID + 32,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Shipping Price',
      parentId: summaryID + 3,
      hierarchyName: "Shipping Price",
      className: '',
      styles: {
        minWidth: '80px',
        textAlign: 'right'
      },
      children: [],
      text: '$0.00',
      attributes: {
        'data-field': 'shipping-price'
      }
    },

    // Tax Item
    {
      id: summaryID + 4,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tax Item',
      parentId: summaryID + 9,
      hierarchyName: "Tax Item",
      className: summaryItemClass,
      styles: {},
      children: [summaryID + 41, summaryID + 42],
      text: '',
      attributes: {
        'data-element': 'tax-row'
      }
    },
    {
      id: summaryID + 41,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tax Label',
      parentId: summaryID + 4,
      hierarchyName: "Tax Label",
      className: '',
      styles: {
        flex: '1'
      },
      children: [],
      text: 'Tax',
      attributes: {
        'data-field': 'tax-label'
      }
    },
    {
      id: summaryID + 42,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tax Amount',
      parentId: summaryID + 4,
      hierarchyName: "Tax Amount",
      className: '',
      styles: {
        minWidth: '80px',
        textAlign: 'right'
      },
      children: [],
      text: '$0.00',
      attributes: {
        'data-field': 'tax-amount'
      }
    },

    // Total Item
    {
      id: summaryID + 5,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Total Item',
      parentId: summaryID + 9,
      hierarchyName: "Total Item",
      className: totalClass,
      styles: {},
      children: [summaryID + 51, summaryID + 52],
      text: '',
      attributes: {
        'data-element': 'total-row'
      }
    },
    {
      id: summaryID + 51,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Total Label',
      parentId: summaryID + 5,
      hierarchyName: "Total Label",
      className: '',
      styles: {
        flex: '1',
        fontSize: '18px',
        fontWeight: '600'
      },
      children: [],
      text: 'Total',
      attributes: {
        'data-field': 'total-label'
      }
    },
    {
      id: summaryID + 52,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Total Amount',
      parentId: summaryID + 5,
      hierarchyName: "Total Amount",
      className: '',
      styles: {
        fontSize: '18px',
        fontWeight: '600',
        minWidth: '80px',
        textAlign: 'right'
      },
      children: [],
      text: '$0.00',
      attributes: {
        'data-field': 'total-amount'
      }
    }
  ];
  
  // Return all elements
  return {
    checkoutSection: newElements[0],
    elements: newElements
  };
};

export default createCheckoutElements; 